import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Запись голоса для Штаба в PWA (ТЗ стр. 24, «Микрофон PWA»; SC09).
 *
 * - Доступ к микрофону спрашивается только по нажатию «Записать голос»: ни при
 *   открытии чата, ни заранее через Permissions API.
 * - Каждая неудача видна: нет поддержки, отказ, нет устройства, микрофон занят,
 *   запись прервалась (звонок, другое приложение забрало микрофон, ушли со
 *   страницы). Прерванная запись не отправляется: кусок фразы опаснее, чем
 *   ничего.
 * - Отмена - не ошибка: возвращаемся к полю ввода без сообщения.
 */

export type VoiceProblem = 'unsupported' | 'denied' | 'no_device' | 'busy' | 'interrupted' | 'too_short'

export type VoicePhase = 'idle' | 'starting' | 'recording'

/** Короче этого запись не отправляем: почти наверняка случайное нажатие. */
const MIN_RECORDING_MS = 800
/** Длиннее - останавливаем сами и отправляем то, что есть. */
export const MAX_RECORDING_SECONDS = 120

export const VOICE_PROBLEM_TEXT: Record<VoiceProblem, string> = {
  unsupported: 'Этот браузер не умеет записывать голос. Напишите вопрос текстом.',
  denied:
    'Нет доступа к микрофону. Разрешить его можно в настройках браузера для этого сайта. Текстом писать можно и без микрофона.',
  no_device: 'Микрофон не найден. Напишите вопрос текстом.',
  busy: 'Микрофон занят другим приложением. Освободите его и попробуйте ещё раз.',
  interrupted: 'Запись прервалась и не отправлена. Запишите ещё раз.',
  too_short: 'Запись слишком короткая. Нажмите «Записать голос», скажите вопрос и нажмите «Готово».',
}

/** Ошибка getUserMedia в понятную причину. */
export function problemFromError(err: unknown): VoiceProblem {
  const name = (err as { name?: string } | null)?.name ?? ''
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'denied'
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'no_device'
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'busy'
    default:
      return 'busy'
  }
}

function pickMime(): string | undefined {
  const R = (globalThis as { MediaRecorder?: { isTypeSupported?(t: string): boolean } }).MediaRecorder
  for (const t of ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4', 'audio/webm']) {
    if (R?.isTypeSupported?.(t)) return t
  }
  return undefined
}

export function voiceSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof (globalThis as { MediaRecorder?: unknown }).MediaRecorder === 'function'
  )
}

/** onRecorded получает запись и её длительность в миллисекундах. */
export function useVoiceInput(onRecorded: (audio: Blob, durationMs: number) => void) {
  const [phase, setPhase] = useState<VoicePhase>('idle')
  const [problem, setProblem] = useState<VoiceProblem | null>(null)
  const [seconds, setSeconds] = useState(0)

  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval>>()
  const startedAt = useRef(0)
  // Номер попытки: ответ разрешения после отмены или ухода не должен включить запись.
  const attempt = useRef(0)
  const onRecordedRef = useRef(onRecorded)
  onRecordedRef.current = onRecorded

  const release = useCallback(() => {
    clearInterval(timerRef.current)
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    const rec = recorderRef.current
    recorderRef.current = null
    if (rec) {
      rec.ondataavailable = null
      rec.onstop = null
      rec.onerror = null
    }
    return rec
  }, [])

  /** Выбросить запись и показать причину (или молча, если причины нет - отмена). */
  const discard = useCallback(
    (reason: VoiceProblem | null) => {
      attempt.current += 1
      const rec = release()
      if (rec && rec.state !== 'inactive') {
        try {
          rec.stop()
        } catch {
          // уже остановлен
        }
      }
      setPhase('idle')
      setSeconds(0)
      setProblem(reason)
    },
    [release],
  )

  const stop = useCallback(() => {
    const rec = recorderRef.current
    if (!rec || rec.state === 'inactive') return
    const chunks = chunksRef.current
    const durationMs = Date.now() - startedAt.current
    const tooShort = durationMs < MIN_RECORDING_MS
    rec.onstop = () => {
      const type = rec.mimeType || chunks[0]?.type || 'audio/webm'
      const blob = new Blob(chunks, { type })
      release()
      setPhase('idle')
      setSeconds(0)
      if (tooShort || blob.size === 0) {
        setProblem('too_short')
        return
      }
      onRecordedRef.current(blob, durationMs)
    }
    try {
      rec.stop()
    } catch {
      discard('interrupted')
    }
  }, [discard, release])

  const start = useCallback(async () => {
    if (phase !== 'idle') return
    setProblem(null)
    if (!voiceSupported()) {
      setProblem('unsupported')
      return
    }
    const my = ++attempt.current
    setPhase('starting')

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (err) {
      if (attempt.current !== my) return
      setPhase('idle')
      setProblem(problemFromError(err))
      return
    }
    if (attempt.current !== my) {
      // Пока спрашивали разрешение, запись отменили или ушли с экрана
      stream.getTracks().forEach((t) => t.stop())
      return
    }

    let rec: MediaRecorder
    try {
      const mimeType = pickMime()
      rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
    } catch {
      stream.getTracks().forEach((t) => t.stop())
      setPhase('idle')
      setProblem('unsupported')
      return
    }

    const chunks: Blob[] = []
    chunksRef.current = chunks
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunks.push(e.data)
    }
    rec.onerror = () => discard('interrupted')
    // Микрофон забрали (звонок, другое приложение, отключили гарнитуру)
    stream.getTracks().forEach((t) => {
      t.onended = () => {
        if (recorderRef.current === rec) discard('interrupted')
      }
    })

    streamRef.current = stream
    recorderRef.current = rec
    try {
      rec.start(250)
    } catch {
      discard('busy')
      return
    }
    startedAt.current = Date.now()
    setSeconds(0)
    setPhase('recording')
    timerRef.current = setInterval(() => {
      const s = Math.floor((Date.now() - startedAt.current) / 1000)
      setSeconds(s)
    }, 500)
  }, [discard, phase])

  // Предел длины: останавливаем и отправляем, что успели сказать
  useEffect(() => {
    if (phase === 'recording' && seconds >= MAX_RECORDING_SECONDS) stop()
  }, [phase, seconds, stop])

  // Ушли со страницы или свернули приложение посреди записи: браузер на
  // телефоне всё равно оборвёт микрофон, честнее сказать об этом сразу.
  useEffect(() => {
    if (phase !== 'recording') return
    const onHide = () => {
      if (document.visibilityState === 'hidden') discard('interrupted')
    }
    document.addEventListener('visibilitychange', onHide)
    return () => document.removeEventListener('visibilitychange', onHide)
  }, [discard, phase])

  // Уход с экрана: микрофон освобождается, запись не отправляется
  useEffect(
    () => () => {
      attempt.current += 1
      const rec = release()
      if (rec && rec.state !== 'inactive') {
        try {
          rec.stop()
        } catch {
          // уже остановлен
        }
      }
    },
    [release],
  )

  return {
    phase,
    seconds,
    problem,
    start,
    stop,
    cancel: () => discard(null),
    clearProblem: () => setProblem(null),
  }
}
